import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MdFactory,
  MdSupervisorAccount,
  MdEngineering,
  MdAssignment,
  MdEdit,
  MdDomain,
  MdPeople,
  MdSummarize,
  MdList,
  MdArrowForward,
  MdChevronRight,
  MdRefresh,
} from "react-icons/md";
import { useWorkers, ROLES } from "context/WorkersContext";
import { getSectors } from "api/sectors";
import { getEvaluationScores } from "api/evaluationScores";
import UpcomingTrainingEvents from "./UpcomingTrainingEvents";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";
const BASE = "/performance-kpi";

function formatRelative(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const evDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today - evDay) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

function avgScore(ev) {
  const s = ev.kpiScores && Object.values(ev.kpiScores);
  if (!s?.length) return null;
  return (s.reduce((a, b) => a + b, 0) / s.length).toFixed(1);
}

export default function PerformanceKPIDashboard() {
  const navigate = useNavigate();
  const { workers } = useWorkers();

  // ── Sectors from API ──
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    try {
      const result = await getSectors();
      const list = (result.data || []).map((s) => ({
        id: s.id,
        name: s.name,
        description: s.description || "",
      }));
      setSectors(list);
    } catch (err) {
      console.error("Failed to load sectors:", err);
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // ── Evaluations from API ──
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationsLoading, setEvaluationsLoading] = useState(true);

  const fetchEvaluations = useCallback(async () => {
    setEvaluationsLoading(true);
    try {
      const result = await getEvaluationScores();
      const flatScores = result.data || [];

      // Group flat API scores into structured evaluation records
      const grouped = flatScores.reduce((acc, score) => {
        const evaluationId = score.evaluation_id;
        if (!acc[evaluationId]) {
          acc[evaluationId] = {
            id: evaluationId,
            evaluateeId: score.evaluatee_id,
            evaluateeName: score.evaluatee_name,
            sectorId: score.sector_id,
            sectorName: score.sector_name,
            evaluateeRole: score.evaluatee_role,
            date: score.evaluated_at,
            kpiScores: {},
          };
        }
        acc[evaluationId].kpiScores[score.kpi_id] = score.score_range;
        return acc;
      }, {});

      // Sort by date descending (most recent first)
      const sorted = Object.values(grouped).sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setEvaluations(sorted);
    } catch (err) {
      console.error("Failed to load evaluations:", err);
      setEvaluations([]);
    } finally {
      setEvaluationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvaluations();
  }, [fetchEvaluations]);

  // ── Derived stats ──
  const supervisorsCount = useMemo(
    () => workers.filter((w) => w.role === ROLES.SUPERVISOR).length,
    [workers]
  );
  const operatorsCount = useMemo(
    () => workers.filter((w) => w.role === ROLES.OPERATOR).length,
    [workers]
  );

  // Sector stats: count workers and evaluations per sector using real data
  const sectorStats = useMemo(() => {
    return sectors.map((s) => {
      // Match workers by sector_name
      const personnelCount = workers.filter(
        (w) => w.sector_name && w.sector_name.toLowerCase() === s.name.toLowerCase()
      ).length;
      // Match evaluations by sectorName or sectorId
      const evalCount = evaluations.filter(
        (e) =>
          (e.sectorName && e.sectorName.toLowerCase() === s.name.toLowerCase()) ||
          e.sectorId === s.id
      ).length;
      return { ...s, personnelCount, evalCount };
    });
  }, [sectors, workers, evaluations]);

  const recentEvaluations = useMemo(
    () => evaluations.slice(0, 6),
    [evaluations]
  );

  const isLoading = sectorsLoading || evaluationsLoading;

  const handleRefresh = () => {
    fetchSectors();
    fetchEvaluations();
  };

  const quickActions = [
    { icon: MdEdit, label: "Evaluate", path: `${BASE}/evaluation`, color: SECONDARY },
    { icon: MdDomain, label: "Sector Management", path: `${BASE}/sector-management`, color: PRIMARY },
    { icon: MdPeople, label: "Workers", path: `${BASE}/workers`, color: "#059669" },
    { icon: MdSummarize, label: "Reports & Analytics", path: `${BASE}/reports`, color: "#7c3aed" },
    { icon: MdList, label: "Evaluation records", path: `${BASE}/evaluation-records`, color: "#dc2626" },
  ];

  const metrics = [
    { label: "Sectors", value: sectors.length, icon: MdFactory, color: PRIMARY },
    { label: "Supervisors", value: supervisorsCount, icon: MdSupervisorAccount, color: SECONDARY },
    { label: "Operators", value: operatorsCount, icon: MdEngineering, color: "#059669" },
    { label: "Evaluations", value: evaluations.length, icon: MdAssignment, color: "#7c3aed" },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <span
            className="inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest mb-2"
            style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
          >
            Performance KPI
          </span>
          <h1 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Operational Overview
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base mt-1 max-w-2xl">
            Metrics, quick actions, and recent activity for sectors, workers, and evaluations.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isLoading}
          className="mt-2 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors disabled:opacity-50"
          title="Refresh data"
        >
          <MdRefresh className={`text-xl ${isLoading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.label}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider">
                  {m.label}
                </p>
                <span
                  className="p-2 rounded-xl text-white"
                  style={{ backgroundColor: `${m.color}20`, color: m.color }}
                >
                  <Icon className="text-lg" />
                </span>
              </div>
              <p className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black mt-2 tracking-tight">
                {m.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
        <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800">
          <h2 className="font-black text-slate-900 dark:text-white text-lg">Quick actions</h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            Jump to key areas of the module
          </p>
        </div>
        <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.label}
                type="button"
                onClick={() => navigate(a.path)}
                className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 hover:border-[#0e76a8]/40 hover:bg-[#0e76a8]/5 dark:hover:bg-[#0e76a8]/10 transition-all text-left group"
              >
                <span
                  className="p-2.5 rounded-xl shrink-0"
                  style={{ backgroundColor: `${a.color}18`, color: a.color }}
                >
                  <Icon className="text-xl" />
                </span>
                <span className="flex-1 font-bold text-slate-800 dark:text-slate-200 text-sm">
                  {a.label}
                </span>
                <MdChevronRight className="text-slate-400 group-hover:text-[#0e76a8] shrink-0 text-xl" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Upcoming Training Events */}
      <UpcomingTrainingEvents />

      {/* Two-column: Recent evaluations + Sector overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Recent evaluations */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm flex flex-col">
          <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h2 className="font-black text-slate-900 dark:text-white text-lg">Recent evaluations</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
                Latest evaluations
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`${BASE}/evaluation-records`)}
              className="text-sm font-bold flex items-center gap-1 hover:underline"
              style={{ color: PRIMARY }}
            >
              View all <MdArrowForward className="text-base" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5">
            {evaluationsLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-[#0e76a8]" />
              </div>
            ) : recentEvaluations.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-sm py-6 text-center">
                No evaluations yet. Submit from <strong>Evaluate</strong>.
              </p>
            ) : (
              <ul className="space-y-3">
                {recentEvaluations.map((ev) => (
                  <li
                    key={ev.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800"
                  >
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white text-sm truncate">
                        {ev.evaluateeName || ev.operatorName}
                      </p>
                      <p className="text-slate-500 dark:text-slate-400 text-xs truncate">
                        {ev.sectorName} · {formatRelative(ev.date)}
                      </p>
                    </div>
                    {avgScore(ev) != null && (
                      <span
                        className="shrink-0 px-2.5 py-1 rounded-lg text-xs font-black text-white"
                        style={{ backgroundColor: PRIMARY }}
                      >
                        {avgScore(ev)} avg
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Sector overview */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm flex flex-col">
          <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h2 className="font-black text-slate-900 dark:text-white text-lg">Sector overview</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
                Personnel and evaluations per sector
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`${BASE}/sector-management`)}
              className="text-sm font-bold flex items-center gap-1 hover:underline"
              style={{ color: PRIMARY }}
            >
              Manage <MdArrowForward className="text-base" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5">
            {sectorsLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-[#0e76a8]" />
              </div>
            ) : sectors.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-sm py-6 text-center">
                No sectors found. Create one in <strong>Sector Management</strong>.
              </p>
            ) : (
              <ul className="space-y-3">
                {sectorStats.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30"
                  >
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white text-sm">
                        {s.name}
                      </p>
                      <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                        {s.personnelCount} assigned · {s.evalCount} evaluation{s.evalCount !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                        {s.personnelCount} staff
                      </span>
                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded">
                        {s.evalCount} evals
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
